import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { authApi } from '../api/auth';
import { setAuthToken } from '../api/client';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const hasVerifiedRef = useRef(false);
  
  const token = searchParams.get('token');

  const verifyEmail = useCallback(async () => {
    // Prevent multiple calls
    if (hasVerifiedRef.current) {
      return;
    }
    
    if (!token) {
      setError('Geçersiz onay linki');
      setLoading(false);
      return;
    }

    hasVerifiedRef.current = true;
    try {
      if (!token) return;
      const data = await authApi.verifyEmail(token);
        
        if (data.success) {
          setSuccess(true);
          // Backend response: { success: true, data: { access_token: "...", user: {...} } }
          const userData = data.data?.user || data.data;
          setEmail(userData?.email || '');
          
          if (data.data?.access_token) {
            // Token'ı localStorage'a kaydet (senkron işlem)
            localStorage.setItem('authToken', data.data.access_token);
            // Token'ı axios header'ına da set et
            setAuthToken(data.data.access_token);
            
            console.log('Token saved, redirecting to onboarding...');
          }
          
          // window.location kullanarak sayfayı tamamen yenile
          // Sayfa yenilendiğinde AuthContext'in useEffect'i çalışacak ve
          // localStorage'dan token'ı okuyup getProfile() çağıracak
          // User state yüklendiğinde App.tsx routing mantığı şunu kontrol edecek:
          // if (user.email_verified && !user.is_onboarding_done) -> Onboarding gösterilir
          // Aksi halde dashboard gösterilir
          setTimeout(() => {
            window.location.href = '/onboarding';
          }, 2000);
        } else {
          setError(data.error?.message || 'Email onaylanamadı');
        }
    } catch (err: any) {
      hasVerifiedRef.current = false; // Reset on error so user can retry
      const errorMessage = err?.response?.data?.error?.message || err?.response?.data?.message || 'Sunucuya bağlanırken hata oluştu';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyEmail();
  }, [verifyEmail]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Email Onaylanıyor...</h2>
          <p className="text-gray-600">Lütfen bekleyin</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-red-100">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Onay Başarısız
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {error}
            </p>
          </div>

          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <button
                onClick={() => window.location.href = '/login'}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Giriş Sayfasına Dön
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Email Onaylandı!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {email && (
                <>
                  <span className="font-medium">{email}</span> adresi başarıyla onaylandı.
                </>
              )}
            </p>
          </div>

          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mb-6">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Hoş Geldiniz!
                </h3>
                <p className="text-gray-600">
                  Hesabınız aktifleştirildi. Şimdi şifrenizi oluşturabilirsiniz.
                </p>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => window.location.href = '/onboarding'}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Şifre Oluştur
                </button>

                <p className="text-xs text-gray-500">
                  Otomatik olarak şifre oluşturma sayfasına yönlendiriliyorsunuz...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
